import { useState, useEffect } from "react";
import { arrayMoveImmutable } from "array-move";
import "./App.css";

const API_KEY_MAPPING = [
  { apiKey: "ALTIN", displayName: "HAS ALTIN" },
  { apiKey: "995HAS", displayName: "995 HAS ALTIN" },
  { apiKey: "stl2", displayName: "ÇEYREK" },
  { apiKey: "stl4", displayName: "YARIM" },
  { apiKey: "stl6", displayName: "TEKLİK" },
  { apiKey: "gram13", displayName: "1 GRAM 24 AYAR" },
  { apiKey: "stl10", displayName: "ATA LİRA" },
];

const defaultItems = API_KEY_MAPPING.map((item) => ({
  apiKey: item.apiKey,
  displayName: item.displayName,
  visible: true,
}));

export default function TVSettings() {
  const [items, setItems] = useState(defaultItems);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "TV Ayarları - Buğra Toprak Kuyumculuk";
    fetch("/api/tv-settings")
      .then((res) => res.json())
      .then((data) => {
        if (data.items && Array.isArray(data.items)) {
          // Sunucudan gelen sıraya göre oluştur, yeni eklenen ürünleri sona ekle
          const merged = [];
          for (const saved of data.items) {
            const def = API_KEY_MAPPING.find((d) => d.apiKey === saved.apiKey);
            if (def) {
              merged.push({
                apiKey: saved.apiKey,
                displayName: def.displayName,
                visible: saved.visible,
              });
            }
          }
          // Kayıtlı ayarlarda olmayan yeni ürünleri ekle
          for (const def of API_KEY_MAPPING) {
            if (!merged.find((m) => m.apiKey === def.apiKey)) {
              merged.push({
                apiKey: def.apiKey,
                displayName: def.displayName,
                visible: true,
              });
            }
          }
          setItems(merged);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const moveUp = (index) => {
    if (index === 0) return;
    setItems(arrayMoveImmutable(items, index, index - 1));
    setSaved(false);
  };

  const moveDown = (index) => {
    if (index === items.length - 1) return;
    setItems(arrayMoveImmutable(items, index, index + 1));
    setSaved(false);
  };

  const toggleVisible = (index) => {
    setItems(
      items.map((item, i) =>
        i === index ? { ...item, visible: !item.visible } : item
      )
    );
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map(({ apiKey, visible }) => ({ apiKey, visible })),
      };
      const res = await fetch("/api/tv-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error("Kaydetme hatası:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0a0a0a] text-[#f0e6d2]">
        <div className="spinner" style={{ width: "3rem", height: "3rem" }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f0e6d2] p-4 md:p-8">
      {/* Başlık */}
      <div className="max-w-xl mx-auto">
        <h1 className="font-display text-2xl md:text-3xl font-bold gold-text tracking-wide text-center mb-6">
          TV Ekran Ayarları
        </h1>

        {/* Ürün Listesi */}
        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={item.apiKey}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${
                item.visible
                  ? "bg-gradient-to-r from-gray-900/60 to-black/40 border-gray-700/50"
                  : "bg-gray-900/20 border-gray-800/30 opacity-50"
              }`}
            >
              {/* Sıra numarası */}
              <span className="text-gray-500 text-sm w-5 text-center shrink-0">
                {index + 1}
              </span>

              {/* Yukarı/Aşağı butonları */}
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className={`p-1 rounded transition-all ${
                    index === 0
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gold hover:bg-gold/20"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === items.length - 1}
                  className={`p-1 rounded transition-all ${
                    index === items.length - 1
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gold hover:bg-gold/20"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Ürün adı */}
              <span className={`flex-grow font-semibold text-sm md:text-base ${item.visible ? "gold-text" : "text-gray-500"}`}>
                {item.displayName}
              </span>

              {/* Görünürlük toggle */}
              <button
                onClick={() => toggleVisible(index)}
                className={`shrink-0 w-12 h-7 rounded-full transition-all duration-300 relative ${
                  item.visible ? "bg-[#d4af37]" : "bg-gray-700"
                }`}
              >
                <div
                  className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all duration-300 ${
                    item.visible ? "left-5.5" : "left-0.5"
                  }`}
                  style={{ left: item.visible ? "22px" : "2px" }}
                ></div>
              </button>
            </div>
          ))}
        </div>

        {/* Kaydet Butonu */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full py-3 px-6 rounded-lg font-bold text-lg transition-all duration-300 ${
              saving
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-[#d4af37] hover:bg-[#c5a028] text-black hover:shadow-lg hover:shadow-[#d4af37]/20"
            }`}
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
          {saved && (
            <div className="flex items-center gap-2 text-green-400 animate-in slide-in-from-bottom duration-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Ayarlar kaydedildi</span>
            </div>
          )}
        </div>

        {/* TV sayfasına link */}
        <div className="mt-6 text-center">
          <a
            href="/tv"
            className="text-gray-400 hover:text-[#d4af37] transition-colors text-sm underline"
          >
            TV Ekranına Git
          </a>
        </div>
      </div>
    </div>
  );
}
